import React, { useState, useEffect } from 'react';
import { Table, Tag, Form, Input, Select, Switch, message, Popconfirm, Typography, Space, Tooltip } from 'antd';
import { useContext } from 'react';
import { Context } from '../context';
import { Head } from './Head';
import { Button, Modal } from 'antd';
import { CheckCircleFilled, EditOutlined, DeleteOutlined, KeyOutlined, EyeOutlined, EyeInvisibleOutlined, CopyOutlined } from '@ant-design/icons';
import { formatDate } from '../utils';

const { Text } = Typography;

const API_BASE_URL = 'https://translation-manag-server.dev.torsunov.pro';

export function People({ onLogout, ...props }) {
  const context = useContext(Context);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();

  // Password management state
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordUser, setPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordForm] = Form.useForm();
  const [passwordMode, setPasswordMode] = useState('generate'); // 'generate' or 'custom'

  // Get current user from localStorage
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const authUserStr = localStorage.getItem('auth_user');
    if (authUserStr) {
      try {
        const authUser = JSON.parse(authUserStr);
        setCurrentUser(authUser);
      } catch (e) {
        console.error('Error parsing auth_user:', e);
      }
    }
  }, []);

  // Check if current user is admin or superadmin
  // Role: 1 = regular, 2 = admin, 3 = superadmin (root)
  const isAdmin = currentUser && (currentUser.role === 2 || currentUser.role === 3);
  const isSuperAdmin = currentUser && currentUser.role === 3;

  // Predefined language groups
  const LANGUAGE_GROUPS = [
    { value: 'Ru', label: '🇷🇺 Ru — Russian' },
    { value: 'En', label: '🇺🇸 En — English' },
    { value: 'Esp', label: '🇪🇸 Esp — Spanish' },
    { value: 'De', label: '🇩🇪 De — German' },
    { value: 'Kz', label: '🇰🇿 Kz — Kazakh' },
    { value: 'Zh', label: '🇨🇳 Zh — Chinese' },
    { value: 'Ar', label: '🇸🇦 Ar — Arabic' },
  ];

  const ROLES = {
    '1': {
      title: 'Обычный',
      color: 'blue'
    },
    '2': {
      title: 'Админ',
      color: 'orange'
    },
    '3': {
      title: 'Супер админ',
      color: 'red'
    }
  }



  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
  };

  // Helper function to translate API error messages to Russian
  const translateError = (errorDetail) => {
    const errorMap = {
      'User with this email already exists': 'Пользователь с таким email уже существует',
      'Invalid email format': 'Неверный формат email',
      'Password too short': 'Пароль слишком короткий',
      'User not found': 'Пользователь не найден',
    };
    return errorMap[errorDetail] || errorDetail;
  };

  const handleAddUser = async (values) => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: values.email,
          password: values.password,
          emailVerified: new Date().toISOString(),
          role: values.role || 3,
          isVerifyByAdmin: values.isVerifyByAdmin || false,
          name: values.name || '',
          groupName: serializeGroups(values.groupName),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(translateError(errorData.detail) || 'Ошибка при создании пользователя');
      }

      message.success('Пользователь успешно добавлен');
      setIsModalOpen(false);
      form.resetFields();

      // Обновляем список пользователей
      if (context.fetchPeople) {
        await context.fetchPeople();
      }
    } catch (error) {
      console.error('Error creating user:', error);
      message.error(error.message || 'Не удалось добавить пользователя');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to parse groupName (comma-separated string to array)
  const parseGroupName = (groupName) => {
    if (!groupName) return [];
    return groupName.split(',').map(g => g.trim()).filter(Boolean);
  };

  // Helper function to serialize groups array to comma-separated string
  const serializeGroups = (groups) => {
    if (!groups || !Array.isArray(groups)) return '';
    return groups.join(',');
  };

  // Get options for select - predefined groups + current user's groups if different
  const getGroupOptions = (currentGroupName) => {
    const currentGroups = parseGroupName(currentGroupName);
    const customGroups = currentGroups.filter(g => !LANGUAGE_GROUPS.find(lg => lg.value === g));
    if (customGroups.length > 0) {
      return [...LANGUAGE_GROUPS, ...customGroups.map(g => ({ value: g, label: `${g} (текущая)` }))];
    }
    return LANGUAGE_GROUPS;
  };

  const showEditModal = (user) => {
    setEditingUser(user);
    editForm.setFieldsValue({
      groupName: parseGroupName(user.groupName),
    });
    setIsEditModalOpen(true);
  };

  const handleEditCancel = () => {
    setIsEditModalOpen(false);
    setEditingUser(null);
    editForm.resetFields();
  };

  const handleUpdateUserGroups = async (values) => {
    if (!editingUser) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_BASE_URL}/users/profile/${editingUser.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          groupName: serializeGroups(values.groupName),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при обновлении пользователя');
      }

      message.success('Группы пользователя обновлены');
      setIsEditModalOpen(false);
      setEditingUser(null);
      editForm.resetFields();

      // Обновляем список пользователей
      if (context.fetchPeople) {
        await context.fetchPeople();
      }
    } catch (error) {
      console.error('Error updating user:', error);
      message.error(error.message || 'Не удалось обновить пользователя');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/users/${userId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при удалении пользователя');
      }

      message.success('Пользователь успешно удалён');

      // Обновляем список пользователей
      if (context.fetchPeople) {
        await context.fetchPeople();
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error(error.message || 'Не удалось удалить пользователя');
    }
  };

  // Password management functions
  const showPasswordModal = (user) => {
    setPasswordUser(user);
    setNewPassword('');
    setShowPassword(false);
    setPasswordMode('generate');
    passwordForm.resetFields();
    setIsPasswordModalOpen(true);
  };

  const handlePasswordCancel = () => {
    setIsPasswordModalOpen(false);
    setPasswordUser(null);
    setNewPassword('');
    setShowPassword(false);
    passwordForm.resetFields();
  };

  const handleGeneratePassword = async () => {
    if (!passwordUser) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/auth/admin/reset-password/${passwordUser.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при сбросе пароля');
      }

      const result = await response.json();
      setNewPassword(result.new_password);
      setShowPassword(true);
      message.success('Пароль успешно сгенерирован');
    } catch (error) {
      console.error('Error generating password:', error);
      message.error(error.message || 'Не удалось сгенерировать пароль');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSetCustomPassword = async (values) => {
    if (!passwordUser) return;

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/auth/admin/set-password/${passwordUser.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ password: values.password }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Ошибка при установке пароля');
      }

      setNewPassword(values.password);
      setShowPassword(true);
      message.success('Пароль успешно установлен');
    } catch (error) {
      console.error('Error setting password:', error);
      message.error(error.message || 'Не удалось установить пароль');
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyPassword = () => {
    if (newPassword) {
      navigator.clipboard.writeText(newPassword);
      message.success('Пароль скопирован в буфер обмена');
    }
  };

  // Check if current user can manage this user's password
  const canManagePassword = (targetUser) => {
    if (!isAdmin) return false;
    // Superadmin can manage anyone
    if (isSuperAdmin) return true;
    // Admin can't manage superadmin passwords
    if (targetUser.role === 3) return false;
    return true;
  };

  const columns = [
    {
      title: 'Одобрен',
      dataIndex: 'isVerifyByAdmin',
      key: 'isVerifyByAdmin',
      render: (value, record = {}, index) => <><Tag color={value ? 'green' : 'red'}>{value === 1 ? <CheckCircleFilled /> : ''}</Tag> {formatDate(record['emailVerified'], true)}</>
    },

    {
      title: 'Роль',
      dataIndex: 'role',
      key: 'role',
      render: (value, record = {}, index) => <><Tag color={ROLES[value]['color']}>{ROLES[value]['title']}</Tag></>
    },

    {
      title: 'Имя',
      dataIndex: 'name',
      key: 'name',
    },
    // {
    //   title: 'Handle',
    //   dataIndex: 'handle',
    //   key: 'handle',
    // },
    {
      title: 'Емейл',
      dataIndex: 'email',
      key: 'email',
    },



    {
      title: 'Группы',
      dataIndex: 'groupName',
      key: 'groupName',
      render: (value) => {
        const groups = parseGroupName(value);
        if (groups.length === 0) return null;
        return (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {groups.map(groupValue => {
              const group = LANGUAGE_GROUPS.find(g => g.value === groupValue);
              return (
                <Tag key={groupValue} color="purple">
                  {group ? group.label : groupValue}
                </Tag>
              );
            })}
          </div>
        );
      },
    },

    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
    },

    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => showEditModal(record)}
            title="Редактировать группы"
          />
          {canManagePassword(record) && (
            <Tooltip title="Управление паролем">
              <Button
                type="text"
                icon={<KeyOutlined />}
                onClick={() => showPasswordModal(record)}
                style={{ color: '#1890ff' }}
              />
            </Tooltip>
          )}
          <Popconfirm
            title="Удалить пользователя"
            description={`Вы уверены, что хотите удалить ${record.name || record.email}?`}
            onConfirm={() => handleDeleteUser(record.id)}
            okText="Да"
            cancelText="Нет"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Удалить пользователя"
            />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <>
      <Modal
        title="Добавить пользователя"
        open={isModalOpen}
        onCancel={handleCancel}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleAddUser}
          initialValues={{
            role: 3,
            isVerifyByAdmin: false,
          }}
          size="small"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Введите email' },
              { type: 'email', message: 'Введите корректный email' },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="password"
            label="Пароль"
            rules={[
              { required: true, message: 'Введите пароль' },
              { min: 6, message: 'Пароль должен быть минимум 6 символов' },
            ]}
            style={{ marginBottom: 12 }}
          >
            <Input.Password placeholder="Введите пароль" />
          </Form.Item>

          <Form.Item
            name="name"
            label="Имя"
            style={{ marginBottom: 12 }}
          >
            <Input placeholder="Иван Иванов" />
          </Form.Item>

          <Form.Item
            name="role"
            label="Роль"
            rules={[{ required: true, message: 'Выберите роль' }]}
            style={{ marginBottom: 12 }}
          >
            <Select>
              <Select.Option value={1}>Обычный</Select.Option>
              <Select.Option value={2}>Админ</Select.Option>
              <Select.Option value={3}>Супер админ</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="groupName"
            label="Языковые группы"
            style={{ marginBottom: 12 }}
          >
            <Select
              mode="multiple"
              placeholder="Выберите языковые группы"
              options={LANGUAGE_GROUPS}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name="isVerifyByAdmin"
            label="Подтверждён администратором"
            valuePropName="checked"
            style={{ marginBottom: 16 }}
          >
            <Switch />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleCancel} style={{ marginRight: 8 }}>
              Отмена
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Добавить
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Groups Modal */}
      <Modal
        title={`Редактировать группы: ${editingUser?.name || editingUser?.email || ''}`}
        open={isEditModalOpen}
        onCancel={handleEditCancel}
        footer={null}
        width={500}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleUpdateUserGroups}
        >
          <Form.Item
            name="groupName"
            label="Языковые группы"
          >
            <Select
              mode="multiple"
              placeholder="Выберите языковые группы"
              options={getGroupOptions(editingUser?.groupName)}
              allowClear
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Button onClick={handleEditCancel} style={{ marginRight: 8 }}>
              Отмена
            </Button>
            <Button type="primary" htmlType="submit" loading={isSubmitting}>
              Сохранить
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Password Management Modal */}
      <Modal
        title={`Управление паролем: ${passwordUser?.name || passwordUser?.email || ''}`}
        open={isPasswordModalOpen}
        onCancel={handlePasswordCancel}
        footer={null}
        width={500}
      >
        {newPassword ? (
          // Show generated/set password
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <Text type="success" strong style={{ fontSize: 16, display: 'block', marginBottom: 16 }}>
              Новый пароль установлен!
            </Text>
            <div style={{
              background: '#f5f5f5',
              padding: '16px',
              borderRadius: '8px',
              marginBottom: 16
            }}>
              <Space size="middle">
                <Text code style={{ fontSize: 18 }}>
                  {showPassword ? newPassword : '••••••••••••'}
                </Text>
                <Button
                  type="text"
                  icon={showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                  onClick={() => setShowPassword(!showPassword)}
                />
                <Tooltip title="Скопировать пароль">
                  <Button
                    type="text"
                    icon={<CopyOutlined />}
                    onClick={copyPassword}
                  />
                </Tooltip>
              </Space>
            </div>
            <Text type="secondary">
              Сохраните этот пароль — он больше не будет показан!
            </Text>
            <div style={{ marginTop: 24 }}>
              <Button type="primary" onClick={handlePasswordCancel}>
                Закрыть
              </Button>
            </div>
          </div>
        ) : (
          // Password management options
          <div>
            <div style={{ marginBottom: 24 }}>
              <Select
                value={passwordMode}
                onChange={setPasswordMode}
                style={{ width: '100%' }}
                size="large"
              >
                <Select.Option value="generate">Сгенерировать новый пароль</Select.Option>
                <Select.Option value="custom">Установить свой пароль</Select.Option>
              </Select>
            </div>

            {passwordMode === 'generate' ? (
              <div style={{ textAlign: 'center' }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  Будет сгенерирован надёжный случайный пароль
                </Text>
                <Button
                  type="primary"
                  size="large"
                  onClick={handleGeneratePassword}
                  loading={isSubmitting}
                  icon={<KeyOutlined />}
                >
                  Сгенерировать пароль
                </Button>
              </div>
            ) : (
              <Form
                form={passwordForm}
                layout="vertical"
                onFinish={handleSetCustomPassword}
              >
                <Form.Item
                  name="password"
                  label="Новый пароль"
                  rules={[
                    { required: true, message: 'Введите пароль' },
                    { min: 6, message: 'Пароль должен быть минимум 6 символов' },
                  ]}
                >
                  <Input.Password placeholder="Введите новый пароль" size="large" />
                </Form.Item>
                <Form.Item
                  name="confirmPassword"
                  label="Подтвердите пароль"
                  dependencies={['password']}
                  rules={[
                    { required: true, message: 'Подтвердите пароль' },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue('password') === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error('Пароли не совпадают'));
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Повторите пароль" size="large" />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
                  <Button onClick={handlePasswordCancel} style={{ marginRight: 8 }}>
                    Отмена
                  </Button>
                  <Button type="primary" htmlType="submit" loading={isSubmitting}>
                    Установить пароль
                  </Button>
                </Form.Item>
              </Form>
            )}
          </div>
        )}
      </Modal>

      <Head activeTab="people" onLogout={onLogout}>
        <Button type="primary" size="middle" onClick={showModal}>
          <div>Добавить пользователя</div>
        </Button>
      </Head>
      <div className="t00 _f _f100" style={{ width: '100%' }}>
        <Table
          dataSource={context.people}
          columns={columns}
          rowKey="_id"
          pagination={false}
        />
      </div>
    </>
  );
}
